import { useCallback, useMemo } from 'react'
import { AlertErrorCircle, ChevronRight, Folder } from '@/assets/icons'
import { CircularProgressContinuousSized } from '@/components/loaders/CircularProgressContinuous'
import type { UserFileRead } from '@/uppy-file-upload/schemas'
import type { FolderResponse } from '@/user_files/folders/schemas'
import { UserFileProcessingStatus } from '@/user_files/schemas/processing-status'
import { isSelectableProcessingStatus } from '@/user_files/utils/processing-status'
import clsx from 'clsx'
import CustomizableTooltip from '@/components/ui/CustomizableTooltip'
import { SearchableText } from '@/components/searchable-text/SearchableText'
import { UserFileTypeIcon } from '@/user_files/files/components/UserFileTypeIcon'
import { DisconnectedFolderIcon, DisconnectedFromProviderTag } from '@/user_files/external-storage/components/DisconnectedStorageItem'
import { EmailAttachmentCount } from '@/user_files/components/EmailAttachmentCount'
import { EmailAttachmentDisclosure } from '@/user_files/components/EmailAttachmentDisclosure'
import { kEmailAttachmentNameIndentPx } from '@/user_files/components/email-attachment-indent'
import { useEmailAttachmentExpansion } from '@/user_files/hooks/useEmailAttachmentExpansion'
import { filterVisibleGroupedFiles, flatSearchGroupedFiles, groupExtractedFiles } from '@/user_files/utils/group-extracted-files'
import { FolderSelectionCheckbox } from './FolderSelectionCheckbox'
import type { FolderSelectionState } from './useRecursiveFolderSelection'

const disabledFileTooltipOffset: [number, number] = [-4, -15]
const unsupportedFileFormatMessage = 'This format is not supported yet.'
const fileProcessingTooltipMessage = 'File is still processing. Try again shortly.'
const fileProcessingFailedMessage = 'Processing failed. Please try again.'
const folderSelectionErrorMessage = 'Unable to select files in this folder. Please try again.'

export type MatterFilesFileToggleOptions = {
  shiftKey?: boolean
}

export type MatterFilesFolderFileListProps = {
  /** Child folders of the browsed folder. */
  folders: FolderResponse[]
  /** Matter root used to identify retained top-level synced folders in the file picker. */
  matterRootFolderId?: string | null
  /** Files in the browsed folder. */
  files: UserFileRead[]
  /** Open a subfolder by id. */
  onNavigateToFolder: (folderId: string) => void
  /** When true, folder row buttons are disabled (e.g. while move is submitting). */
  disabled?: boolean
  /** A list of file ids that are selected. */
  selectedFileIds?: string[]
  /** When provided, file rows are selectable. */
  onToggleFile?: (file: UserFileRead, options?: MatterFilesFileToggleOptions) => void
  /** Controls the input type for selectable rows. Defaults to 'checkbox'. */
  selectionVariant?: 'checkbox' | 'radio'
  /** Optional file-level selection rule. Defaults to allowing every loaded file. */
  isFileSelectable?: (file: UserFileRead) => boolean
  includeProcessingFiles?: boolean
  /** Optional detected-format compatibility rule. Rejections use the standard unsupported-format tooltip. */
  isFileFormatSupported?: (file: UserFileRead) => boolean
  /** Optional tooltip shown for files rejected by `isFileSelectable`. */
  fileNotSelectableMessage?: string | ((file: UserFileRead) => string | undefined)
  /** Get recursive selection state for a child folder row. */
  getFolderSelectionState?: (folderId: string) => FolderSelectionState
  /** Get tooltip copy for a child folder's recursive selection state. */
  getFolderSelectionTooltipMessage?: (folderId: string) => string | null
  /** Toggle recursive file selection for a child folder row. */
  onToggleFolderSelection?: (folder: FolderResponse) => void
  /**
   * Full location per row id. Search rows with a path (or parent-email context) render as two
   * lines under the name. When both are known, “Attachment of …” wins; otherwise the folder path
   * is shown — results can come from anywhere in the matter, so the name alone is ambiguous.
   */
  locationPathById?: ReadonlyMap<string, string>
  /** Query whose matched substrings are bolded in row names. */
  searchQuery?: string
}

export function MatterFilesFolderFileList({
  folders,
  matterRootFolderId,
  files,
  onNavigateToFolder,
  disabled = false,
  selectedFileIds = [],
  onToggleFile,
  selectionVariant = 'checkbox',
  isFileSelectable,
  includeProcessingFiles = false,
  isFileFormatSupported,
  fileNotSelectableMessage,
  getFolderSelectionState,
  getFolderSelectionTooltipMessage,
  onToggleFolderSelection,
  locationPathById,
  searchQuery,
}: MatterFilesFolderFileListProps) {
  const hasItems = folders.length > 0 || files.length > 0
  const showFolderCheckboxes = Boolean(onToggleFile && onToggleFolderSelection && getFolderSelectionState && selectionVariant === 'checkbox')
  const isSearch = searchQuery !== undefined
  // Best-effort key: this list only receives files/folders as props (no currentFolderId), so empty
  // folders fall back to folders[0]?.parent_id or 'root'. MatterFilesPanel keys off the real folder id.
  const expansionResetKey = `${files[0]?.folder_id ?? folders[0]?.parent_id ?? 'root'}::${searchQuery ?? ''}`
  const { expandedIds, isExpanded, toggleExpanded } = useEmailAttachmentExpansion(expansionResetKey)
  const toggleAttachments = useCallback((fileId: string) => () => toggleExpanded(fileId), [toggleExpanded])

  const fileRows = useMemo(() => {
    if (isSearch) return flatSearchGroupedFiles(files)
    const grouped = groupExtractedFiles(files)
    return filterVisibleGroupedFiles(grouped, expandedIds)
  }, [files, isSearch, expandedIds])

  return (
    <div
      // Keep the expand control under the pointer when attachment rows are removed below it.
      className="flex-1 min-h-0 overflow-y-auto overscroll-y-contain [overflow-anchor:none] -mb-px [container-type:inline-size]"
      role="region"
      aria-label="Folders and files in this location"
    >
      <ul className={clsx('m-0 list-none divide-y divide-brand-neutral-200 p-0', hasItems && 'border-b border-brand-neutral-200')}>
        {folders.map((folder) => {
          const disconnectedRootMetadata =
            matterRootFolderId && folder.parent_id === matterRootFolderId && folder.external_storage?.tombstoned ? folder.external_storage : undefined
          const folderLocationPath = locationPathById?.get(folder.id)
          const folderSelectionState = getFolderSelectionState?.(folder.id) ?? 'unchecked'
          const folderSelectionIsLoading = folderSelectionState === 'loading'
          const folderCheckboxTooltip = (() => {
            if (folderSelectionState === 'error') {
              return <span className="text-sm px-1 py-2 font-normal font-sans">{folderSelectionErrorMessage}</span>
            }
            return ''
          })()
          const folderSelectionTooltipMessage = folderSelectionState === 'partial-selected' ? getFolderSelectionTooltipMessage?.(folder.id) : null
          const folderRowTooltip = folderSelectionTooltipMessage ? (
            <span className="text-sm px-1 py-2 font-normal font-sans">{folderSelectionTooltipMessage}</span>
          ) : (
            ''
          )

          return (
            <li key={folder.id}>
              <CustomizableTooltip title={folderRowTooltip} placement="top-start" offset={disabledFileTooltipOffset} disableInteractive>
                <div
                  className={clsx(
                    'flex w-full items-center gap-3 pl-6 pr-3 hover:bg-brand-neutral-50',
                    disconnectedRootMetadata ? 'min-h-12' : folderLocationPath ? 'h-[54px]' : 'h-12'
                  )}
                >
                  {showFolderCheckboxes ? (
                    <CustomizableTooltip title={folderCheckboxTooltip} placement="top-start" offset={disabledFileTooltipOffset} disableInteractive>
                      <label className={clsx('flex h-full shrink-0 items-center', folderSelectionIsLoading ? 'cursor-not-allowed' : 'cursor-pointer')}>
                        <FolderSelectionCheckbox
                          disabled={folderSelectionIsLoading}
                          folderName={folder.name}
                          onToggle={() => onToggleFolderSelection?.(folder)}
                          state={folderSelectionState}
                        />
                      </label>
                    </CustomizableTooltip>
                  ) : null}
                  {disconnectedRootMetadata ? (
                    <div className="relative flex min-w-0 flex-1 items-center gap-3 py-3">
                      <span className="relative flex size-6 shrink-0" aria-hidden>
                        <DisconnectedFolderIcon provider={disconnectedRootMetadata.provider} />
                        {folderSelectionIsLoading ? (
                          <span className="absolute inset-0 flex translate-y-0.5 items-center justify-center">
                            <CircularProgressContinuousSized size={10} thickness={8} showTrack />
                          </span>
                        ) : null}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex min-w-0 flex-col items-start gap-0.5 [@container_(min-width:_640px)]:flex-row [@container_(min-width:_640px)]:items-center [@container_(min-width:_640px)]:gap-2">
                          {/* Extend the name button across the row; the separate chip stays above its hit area. */}
                          <button
                            type="button"
                            className="min-w-0 max-w-full truncate text-left text-sm text-brand-neutral-950 after:absolute after:inset-0 after:z-10 focus-visible:outline-hidden focus-visible:after:ring-2 focus-visible:after:ring-brand-500 focus-visible:after:ring-inset"
                            onClick={() => onNavigateToFolder(folder.id)}
                            disabled={disabled}
                            aria-label={folderLocationPath ? `${folder.name}, ${folderLocationPath}` : folder.name}
                            title={folder.name}
                          >
                            <SearchableText text={folder.name} searchQuery={searchQuery ?? ''} />
                          </button>
                          <span className="relative z-20 flex shrink-0">
                            <DisconnectedFromProviderTag provider={disconnectedRootMetadata.provider} disconnectedAt={disconnectedRootMetadata.updated_at} />
                          </span>
                        </div>
                        {folderLocationPath ? (
                          <span className="block truncate text-xs leading-4 text-brand-neutral-600" title={folderLocationPath}>
                            {folderLocationPath}
                          </span>
                        ) : null}
                      </div>
                      <ChevronRight className="h-5 w-5 shrink-0 text-brand-neutral-950" />
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="flex h-full min-w-0 flex-1 items-center gap-3 text-left focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-inset"
                      onClick={() => onNavigateToFolder(folder.id)}
                      disabled={disabled}
                      // Highlighting splits the name across elements, which the accessible-name
                      // calculation joins with spaces. Name the row explicitly so it is still read out
                      // as the folder it is, with the location that disambiguates it.
                      aria-label={folderLocationPath ? `${folder.name}, ${folderLocationPath}` : undefined}
                    >
                      <span className="relative h-6 w-6 shrink-0" aria-hidden="true">
                        <Folder className="h-6 w-6 shrink-0 text-brand-neutral-500" />
                        {folderSelectionIsLoading ? (
                          <span className="absolute inset-0 flex translate-y-0.5 items-center justify-center">
                            <CircularProgressContinuousSized size={10} thickness={8} showTrack />
                          </span>
                        ) : null}
                      </span>
                      {folderLocationPath ? (
                        <span className="flex min-w-0 flex-1 flex-col">
                          <span className="truncate text-sm text-brand-neutral-950" title={folder.name}>
                            <SearchableText text={folder.name} searchQuery={searchQuery ?? ''} />
                          </span>
                          <span className="truncate text-xs leading-4 text-brand-neutral-600" title={folderLocationPath}>
                            {folderLocationPath}
                          </span>
                        </span>
                      ) : (
                        <span className="min-w-0 flex-1 truncate text-sm text-brand-neutral-950" title={folder.name}>
                          {folder.name}
                        </span>
                      )}
                      <ChevronRight className="h-5 w-5 shrink-0 text-brand-neutral-950" />
                    </button>
                  )}
                </div>
              </CustomizableTooltip>
            </li>
          )
        })}
        {fileRows.map(({ file, visualDepth, parentName, hasChildren, childCount }) => {
          const fileLocationPath = locationPathById?.get(file.id)
          const attachmentDescription = parentName ? `Attachment of ${parentName}` : undefined
          const showParentLabel = isSearch && Boolean(parentName)
          const treePad = visualDepth ? { paddingLeft: `${visualDepth * kEmailAttachmentNameIndentPx}px` } : undefined
          const attachmentCount = childCount

          const fileIcon = (isDisabledRow: boolean) => (
            <UserFileTypeIcon
              detectedContentType={file.detected_content_type}
              detectedExtension={file.detected_extension}
              originalFilename={file.original_filename}
              hasExtractedAttachments={childCount > 0}
              className={clsx('h-6 w-6 shrink-0', isDisabledRow ? 'text-brand-neutral-400' : 'text-brand-neutral-500')}
            />
          )

          const nameBlock = (isDisabledRow: boolean) => (
            <>
              {fileLocationPath || showParentLabel ? (
                <span className="flex min-w-0 flex-col">
                  <span
                    className={clsx('min-w-0 truncate text-sm', isDisabledRow ? 'text-brand-neutral-500' : 'text-brand-neutral-950')}
                    title={file.original_filename}
                  >
                    <SearchableText text={file.original_filename} searchQuery={searchQuery ?? ''} />
                  </span>
                  {showParentLabel ? (
                    <span className="truncate text-xs leading-4 text-brand-neutral-600" title={attachmentDescription}>
                      {attachmentDescription}
                    </span>
                  ) : fileLocationPath ? (
                    <span className="truncate text-xs leading-4 text-brand-neutral-600" title={fileLocationPath}>
                      {fileLocationPath}
                    </span>
                  ) : null}
                </span>
              ) : (
                <span
                  className={clsx('min-w-0 truncate text-sm', isDisabledRow ? 'text-brand-neutral-500' : 'text-brand-neutral-950')}
                  title={file.original_filename}
                >
                  {file.original_filename}
                  {attachmentDescription && !showParentLabel ? <span className="sr-only">, {attachmentDescription}</span> : null}
                </span>
              )}
            </>
          )

          const renderDisclosure = (showHoverTooltip = true) =>
            hasChildren ? (
              <EmailAttachmentDisclosure
                filename={file.original_filename}
                expanded={isExpanded(file.id)}
                onToggle={toggleAttachments(file.id)}
                showHoverTooltip={showHoverTooltip}
              />
            ) : null

          const typeIcon = (isDisabledRow: boolean) => (
            <span className="shrink-0" style={treePad}>
              {fileIcon(isDisabledRow)}
            </span>
          )

          if (!onToggleFile) {
            return (
              <li key={file.id} className="flex h-12 items-center gap-3 pl-6 pr-3">
                {typeIcon(false)}
                <span className="flex min-w-0 flex-1 items-center gap-x-1.5">
                  {nameBlock(false)}
                  {attachmentCount ? <EmailAttachmentCount count={attachmentCount} /> : null}
                  {renderDisclosure()}
                </span>
                <span className="w-5 shrink-0" aria-hidden="true" />
              </li>
            )
          }

          const isSelected = selectedFileIds.includes(file.id)
          const passesFileFilter = isFileSelectable?.(file) ?? true
          // The unsupported format tooltip wins over processing/failure and caller-specific UI so backend-processable but frontend-unsupported files
          // keep the unsupported-format tooltip and default file icon, regardless of whether the backend successfully processes the file or not.
          const passesFormatFilter = isFileFormatSupported?.(file) ?? true
          const processingStatus = file.processing_status
          const isProcessing = processingStatus === UserFileProcessingStatus.Pending || processingStatus === UserFileProcessingStatus.Processing
          const isFailed = processingStatus === UserFileProcessingStatus.Failed
          const isSelectable = isSelectableProcessingStatus(processingStatus, includeProcessingFiles) && passesFormatFilter && passesFileFilter
          const isDisabled = !isSelectable && !isSelected
          const disabledTooltipMessage = (() => {
            if (!passesFormatFilter) return unsupportedFileFormatMessage
            if (!passesFileFilter && typeof fileNotSelectableMessage === 'function') {
              const message = fileNotSelectableMessage(file)
              if (message) return message
            }
            if (isProcessing) return isSelectable ? 'File is still processing.' : fileProcessingTooltipMessage
            if (isFailed) return file.processing_error ?? fileProcessingFailedMessage
            if (!passesFileFilter && typeof fileNotSelectableMessage === 'string') return fileNotSelectableMessage
            return null
          })()
          const disabledTooltip = disabledTooltipMessage ? <span className="text-sm px-1 py-2 font-normal font-sans">{disabledTooltipMessage}</span> : ''

          return (
            <li key={file.id}>
              <CustomizableTooltip title={disabledTooltip} placement="top-start" offset={disabledFileTooltipOffset} disableInteractive>
                <div
                  className={clsx(
                    'flex w-full items-center gap-3 pl-6 pr-3',
                    fileLocationPath || showParentLabel ? 'h-[54px]' : 'h-12',
                    isDisabled ? 'text-brand-neutral-500' : 'hover:bg-brand-neutral-50'
                  )}
                  onMouseDown={(event) => {
                    // Suppress text highlighting during Shift-click range selection
                    if (selectionVariant === 'checkbox' && event.shiftKey) {
                      event.preventDefault()
                    }
                  }}
                >
                  <input
                    type={selectionVariant}
                    name={selectionVariant === 'radio' ? 'file-selection' : undefined}
                    checked={isSelected}
                    disabled={isDisabled}
                    onClick={(event) => onToggleFile(file, selectionVariant === 'checkbox' ? { shiftKey: event.shiftKey } : undefined)}
                    onChange={() => undefined}
                    aria-label={[file.original_filename, attachmentDescription, fileLocationPath].filter(Boolean).join(', ')}
                    className={clsx(
                      'shrink-0 border-brand-neutral-300 text-brand-500 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-inset',
                      selectionVariant === 'radio' ? 'h-5 w-5 rounded-full' : 'h-4 w-4 rounded',
                      isDisabled ? 'cursor-not-allowed disabled:bg-brand-neutral-100' : 'cursor-pointer'
                    )}
                  />
                  <span className="shrink-0" style={treePad}>
                    {!passesFormatFilter ? (
                      fileIcon(isDisabled)
                    ) : isProcessing ? (
                      <CircularProgressContinuousSized size={20} thickness={6} className="shrink-0" />
                    ) : isFailed ? (
                      <AlertErrorCircle className="h-6 w-6 shrink-0 rounded-full bg-red-600 text-white" />
                    ) : (
                      fileIcon(isDisabled)
                    )}
                  </span>
                  <span className="flex min-w-0 flex-1 items-center gap-x-1.5">
                    <button
                      type="button"
                      disabled={isDisabled}
                      className={clsx(
                        'flex min-w-0 items-center gap-2 text-left focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-inset',
                        isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'
                      )}
                      onClick={(event) => onToggleFile(file, selectionVariant === 'checkbox' ? { shiftKey: event.shiftKey } : undefined)}
                    >
                      {nameBlock(isDisabled)}
                    </button>
                    {attachmentCount ? <EmailAttachmentCount count={attachmentCount} /> : null}
                    {renderDisclosure(!isDisabled)}
                  </span>
                  <span className="w-5 shrink-0" aria-hidden="true" />
                </div>
              </CustomizableTooltip>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
